<template>
  <div class="container public">
    <page-header />
    <div class="content-container">
      <div class="coverage-section">
        <h2 class="section-title">{{ $t('emissionCoveragePage.title') }}</h2>
        <p class="text-muted">{{ $t('emissionCoveragePage.intro') }}</p>
        <div class="btn-toolbar">
          <button type="button" class="btn btn-primary mr-3" @click="loadCoverage" :disabled="isFixing">
            <font-awesome-icon icon="rotate" /> &nbsp; {{ $t('emissionCoveragePage.reload') }}
          </button>
        </div>
        <error-row @clearError="clearError" :errorMessage="errorMessage" />
        <div v-show="isLoading" class="is-loading-section">
          <button class="btn btn-primary"><span class="spinner-border spinner-border-sm"></span></button><span> {{ $t('emissionCoveragePage.isLoading') }} </span>
        </div>
        <div v-if="!isLoading && rows.length === 0" id="no-data-section">
          {{ $t('emissionCoveragePage.tableHasNoEntries') }}
        </div>

        <template v-if="!isLoading && rows.length > 0">
          <div class="summary">
            {{ $t('emissionCoveragePage.summary', summary) }}
          </div>

          <div class="card template-card">
            <div class="card-body">
              <h5 class="card-title">{{ $t('emissionCoveragePage.templatesTitle') }}</h5>
              <p class="card-text small text-muted">{{ $t('emissionCoveragePage.templatesHint') }}</p>
              <div class="template-grid">
                <div v-for="type in typesInRows" :key="type" class="form-group mb-1">
                  <label class="small mb-0">{{ typeLabel(type) }}</label>
                  <select class="form-control form-control-sm" v-model="chosenContracts[type]" :disabled="isFixing">
                    <option :value="null">– {{ $t('emissionCoveragePage.noTemplate') }} –</option>
                    <option v-for="sc in contractsForType(type)" :key="sc.id" :value="sc.id">{{ sc.contractName }}<template v-if="sc.companyName"> ({{ sc.companyName }})</template> · {{ sc.co2e }} kg/{{ unitForType(type) }}<template v-if="sc.isDefault"> · {{ $t('emissionCoveragePage.standard') }}</template></option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div class="btn-toolbar fix-toolbar">
            <button type="button" class="btn btn-outline-secondary btn-sm mr-2" @click="selectAllFixable" :disabled="isFixing">{{ $t('emissionCoveragePage.selectAll') }}</button>
            <button type="button" class="btn btn-outline-secondary btn-sm mr-2" @click="selected = {}" :disabled="isFixing">{{ $t('emissionCoveragePage.selectNone') }}</button>
            <button type="button" class="btn btn-warning btn-sm" @click="fixSelected" :disabled="isFixing || selectedRows.length === 0">
              <span v-if="isFixing" class="spinner-border spinner-border-sm"></span>
              {{ $t('emissionCoveragePage.fixSelected', { count: selectedRows.length }) }}
            </button>
            <span v-if="progress.total" class="small ms-3">{{ $t('emissionCoveragePage.progress', progress) }}</span>
          </div>

          <table class="table table-hover table-sm">
            <thead>
              <tr>
                <th>&nbsp;</th>
                <th>{{ $t('emissionCoveragePage.columnOrganization') }}</th>
                <th>{{ $t('emissionCoveragePage.columnFacility') }}</th>
                <th>{{ $t('emissionCoveragePage.columnMedium') }}</th>
                <th class="text-end">{{ $t('emissionCoveragePage.columnSensors') }}</th>
                <th>{{ $t('emissionCoveragePage.columnActive') }}</th>
                <th class="text-end">{{ $t('emissionCoveragePage.columnUnassigned') }}</th>
                <th>{{ $t('emissionCoveragePage.columnPeriod') }}</th>
                <th>{{ $t('emissionCoveragePage.columnAction') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in rows" :key="rowKey(row)" :class="{ 'table-success': results[rowKey(row)] && results[rowKey(row)].ok }">
                <td><input type="checkbox" class="form-check-input" v-model="selected[rowKey(row)]" :disabled="isFixing || !canFix(row)"></td>
                <td>{{ row.organizationCode }}</td>
                <td>{{ row.facilityName }}</td>
                <td>{{ typeLabel(row.type) }}</td>
                <td class="text-end">{{ row.sensors }}</td>
                <td>
                  <span v-if="row.activeEmission" class="badge text-bg-success">{{ $t('emissionCoveragePage.yes') }}</span>
                  <span v-else class="badge text-bg-danger">{{ $t('emissionCoveragePage.no') }}</span>
                </td>
                <td class="text-end">
                  {{ unassigned(row) }}
                  <span v-if="row.foreignEmission" class="small text-muted" :title="$t('emissionCoveragePage.foreignHint')">({{ row.foreignEmission }} {{ $t('emissionCoveragePage.foreign') }})</span>
                </td>
                <td class="small">{{ periodLabel(row) }}</td>
                <td class="small">
                  <template v-if="results[rowKey(row)]">
                    <span v-if="results[rowKey(row)].ok" class="text-success">✓ {{ results[rowKey(row)].message }}</span>
                    <span v-else class="text-danger">{{ results[rowKey(row)].message }}</span>
                  </template>
                  <template v-else>{{ actionLabel(row) }}</template>
                </td>
              </tr>
            </tbody>
          </table>
        </template>
      </div>
    </div>
    <page-footer />
  </div>
</template>

<script>
import moment from 'moment'
import PageHeader from '@/components/PageHeader.vue'
import ErrorRow from '@/components/ErrorRow.vue'
import PageFooter from '@/components/PageFooter.vue'
import PageViewMixin from '@/mixins/PageViewMixin.js'
import coverageService from '@/services/emissionCoverage'
import { defaultContractForType, unassignedCount, fixAction, coverageSummary } from '@/utils/emission-coverage'
import { mapGetters } from 'vuex'

export default {
  name: 'EmissionCoveragePage',
  mixins: [PageViewMixin],
  components: {
    'page-header': PageHeader,
    'page-footer': PageFooter,
    'error-row': ErrorRow
  },
  data () {
    return {
      rows: [],
      isLoading: false,
      isFixing: false,
      errorMessage: '',
      chosenContracts: {}, // type -> supply contract id
      selected: {}, // rowKey -> bool
      results: {}, // rowKey -> { ok, message }
      progress: { done: 0, total: 0 },
      initialized: false
    }
  },
  computed: {
    ...mapGetters(['client', 'supplyContracts']),
    isAdmin () {
      return this.client && this.client.role === 4
    },
    summary () {
      return coverageSummary(this.rows)
    },
    typesInRows () {
      return [...new Set(this.rows.map(r => r.type))].sort()
    },
    selectedRows () {
      return this.rows.filter(r => this.selected[this.rowKey(r)] && this.canFix(r))
    }
  },
  watch: {
    // preselect the standard template (the only one of its medium)
    supplyContracts () {
      this.preselectTemplates()
    },
    // On a direct page load the client data arrives asynchronously
    // (PageHeader dispatches getClientData) — evaluate again once it's there.
    'client.authenticated' () {
      this.evaluateAccess()
    }
  },
  mounted () {
    this.evaluateAccess()
  },
  methods: {
    evaluateAccess () {
      if (!this.client || !this.client.authenticated) {
        // still loading; the 401 interceptor routes logged-out users to /login
        return
      }
      if (!this.isAdmin) {
        this.$router.replace({ name: 'home' })
        return
      }
      if (!this.initialized) {
        this.initialized = true
        this.$store.dispatch('getSupplyContracts')
        this.loadCoverage()
      }
    },
    rowKey (row) {
      return row.facilityId + '-' + row.type
    },
    typeLabel (type) {
      return this.$t('sensorType.' + type)
    },
    unitForType (type) {
      return (type === 0 || type === 3) ? 'kWh' : 'm³'
    },
    contractsForType (type) {
      return (this.supplyContracts || []).filter(c => c.scope === type)
    },
    preselectTemplates () {
      // coverage and templates load in parallel: only decide once the
      // templates are there, and never overwrite a choice already made
      if (!this.supplyContracts || this.supplyContracts.length === 0) return
      for (const type of [0, 1, 2, 3]) {
        if (this.chosenContracts[type] === undefined) {
          const standard = defaultContractForType(this.supplyContracts, type)
          this.chosenContracts[type] = standard ? standard.id : null
        }
      }
    },
    unassigned (row) {
      return unassignedCount(row)
    },
    canFix (row) {
      return row.activeEmission || !!this.chosenContracts[row.type]
    },
    periodLabel (row) {
      if (!row.firstDay) return '–'
      // startofday is local midnight of the facility; format in local time
      const from = moment(row.firstDay).format('DD.MM.YYYY')
      const to = moment(row.lastDay).format('DD.MM.YYYY')
      return from === to ? from : from + ' – ' + to
    },
    actionLabel (row) {
      if (fixAction(row) === 'stamp') {
        return this.$t('emissionCoveragePage.actionStamp')
      }
      const contract = (this.supplyContracts || []).find(c => c.id === this.chosenContracts[row.type])
      return contract
        ? this.$t('emissionCoveragePage.actionCreate', { name: contract.contractName })
        : this.$t('emissionCoveragePage.actionChooseTemplate')
    },
    loadCoverage () {
      this.isLoading = true
      this.errorMessage = ''
      this.results = {}
      this.selected = {}
      this.progress = { done: 0, total: 0 }
      coverageService.listGaps()
        .then((data) => {
          this.rows = (data && data.coverage) ? data.coverage : []
          this.preselectTemplates()
        })
        .catch((error) => {
          this.errorMessage = this.localizedBackendMessage(error.message)
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    selectAllFixable () {
      const next = {}
      for (const row of this.rows) {
        if (this.canFix(row)) next[this.rowKey(row)] = true
      }
      this.selected = next
    },
    // one request per facility/medium (own transaction server-side): short
    // requests, visible progress, a failure only affects its own row
    async fixSelected () {
      const rows = this.selectedRows
      if (!rows.length) return
      if (!window.confirm(this.$t('emissionCoveragePage.confirmFix', { count: rows.length }))) return
      this.isFixing = true
      this.progress = { done: 0, total: rows.length }
      for (const row of rows) {
        const key = this.rowKey(row)
        try {
          const data = await coverageService.fix(row.facilityId, row.type, row.activeEmission ? null : this.chosenContracts[row.type])
          this.results[key] = {
            ok: true,
            message: this.$t(data.created ? 'emissionCoveragePage.resultCreated' : 'emissionCoveragePage.resultStamped', { processed: data.processed })
          }
          this.selected[key] = false
        } catch (error) {
          this.results[key] = { ok: false, message: this.localizedBackendMessage(error.message) }
        }
        this.progress.done++
      }
      this.isFixing = false
    }
  }
}
</script>

<style lang="scss" scoped>
.coverage-section {
  .summary { margin: 0.5rem 0 1rem; font-weight: 600; }
  .template-card { margin-bottom: 1rem; }
  .template-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 0.75rem; }
  .fix-toolbar { margin-bottom: 0.5rem; align-items: center; }
}
</style>
